/**
 * Cmd/Ctrl+Enter saves the open dialog by clicking its primary action.
 * Listens on document (capture) so the shortcut works while the cursor is
 * in a field. React onKeyDown on the dialog panel often never sees those
 * keystrokes because Radix renders the dialog in a portal.
 */

const SKIP_LABEL = /^(cancel|close|dismiss)$/i;

const DIALOG_ROOT_SELECTOR = [
  "[data-radix-dialog-content]",
  "[data-radix-alert-dialog-content]",
  "[role='dialog']",
  "[role='alertdialog']",
].join(", ");

function buttonLabel(btn) {
  return `${btn.getAttribute("aria-label") || ""} ${btn.textContent || ""}`.replace(/\s+/g, " ").trim();
}

function isDocumentLike(node) {
  if (!node || typeof document === "undefined") return true;
  return (
    node === document ||
    node === window ||
    node === document.documentElement ||
    node === document.body
  );
}

export function findDialogPrimaryAction(root) {
  if (!root || typeof root.querySelectorAll !== "function") return null;

  const explicit = root.querySelector("[data-dialog-save]");
  if (explicit && !explicit.disabled) return explicit;

  const footer =
    root.querySelector("[data-dialog-footer]") ||
    root.querySelector(".item-dialog-footer");
  const scope = footer || root;
  const buttons = Array.from(scope.querySelectorAll("button")).filter((btn) => {
    if (btn.disabled) return false;
    const label = buttonLabel(btn);
    if (SKIP_LABEL.test(label)) return false;
    return true;
  });

  if (buttons.length === 0) return null;

  const primary = [...buttons].reverse().find((btn) => {
    const cls = typeof btn.className === "string" ? btn.className : "";
    const testId = btn.getAttribute("data-testid") || "";
    const label = buttonLabel(btn);
    return (
      cls.includes("btn-primary") ||
      cls.includes("bg-primary") ||
      testId.includes("submit") ||
      testId.includes("save") ||
      testId.includes("apply") ||
      /^apply\b/i.test(label)
    );
  });

  return primary || buttons[buttons.length - 1];
}

export function isDialogSaveShortcut(e) {
  if (!e || e.repeat || e.isComposing) return false;
  const isEnter =
    e.key === "Enter" || e.code === "Enter" || e.code === "NumpadEnter";
  return isEnter && !!(e.metaKey || e.ctrlKey);
}

export function findDialogRootFromEvent(e) {
  if (!e) return null;
  const nodes = [e.target, e.currentTarget];
  if (typeof document !== "undefined") nodes.push(document.activeElement);

  for (const node of nodes) {
    if (!node || isDocumentLike(node)) continue;
    if (typeof node.closest === "function") {
      const hit = node.closest(DIALOG_ROOT_SELECTOR);
      if (hit) return hit;
    }
    if (typeof node.querySelector === "function" && findDialogPrimaryAction(node)) {
      return node;
    }
  }

  // Select and date popovers render in their own portal, outside the dialog DOM.
  if (typeof document !== "undefined") {
    const open = document.querySelectorAll(
      "[data-radix-dialog-content], [data-radix-alert-dialog-content], [role='dialog'], [role='alertdialog']"
    );
    const usable = Array.from(open).filter((node) => {
      if (node.hasAttribute("data-shortcut-help-dialog")) return false;
      if (node.getAttribute("data-testid") === "shortcut-help-dialog") return false;
      if (node.getAttribute("data-state") === "closed") return false;
      return true;
    });
    if (usable.length) return usable[usable.length - 1];
  }
  return null;
}

/**
 * @param {KeyboardEvent} e
 * @returns {boolean} true if the event was handled
 */
export function handleDialogSaveKey(e) {
  if (!isDialogSaveShortcut(e) || e.defaultPrevented) return false;
  const root = findDialogRootFromEvent(e);
  if (!root) return false;
  if (root.hasAttribute("data-shortcut-help-dialog")) return false;
  const btn = findDialogPrimaryAction(root);
  if (!btn) return false;
  e.preventDefault();
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (
    active &&
    root.contains(active) &&
    active !== btn &&
    typeof active.blur === "function"
  ) {
    active.blur();
  }
  btn.click();
  return true;
}

let listening = false;

export function onDocumentDialogSaveKey(e) {
  return handleDialogSaveKey(e);
}

export function installDialogSaveKeyListener() {
  if (listening || typeof document === "undefined") return;
  listening = true;
  // Capture so a field that stops bubbling cannot swallow the shortcut.
  document.addEventListener("keydown", onDocumentDialogSaveKey, true);
}
